import React from 'react';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import colors from 'src/styles/colors.module.scss';
import styles from './manageEmailPreferences.module.scss';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import adobeTags from 'src/types/adobe-tags';
import ManageEmailPreferencesRow from 'src/components/atomic/molecules/manageEmailPreferencesRow/ManageEmailPreferencesRow';
import {
    useUpdateEmailPreferences,
    useEmailPreferences,
    useMaskedAccountInfo
} from 'src/hooks/queries/email-preferences';
import { trackClick } from 'src/hooks/useAdobeTags';
import { EmailPreferencesModel, MaskedAccountInfoModel } from 'src/types/email-preferences.model';
import ErrorBlock from '../../molecules/errorBlock/ErrorBlock';
import Selectors from 'src/utils/selectors';
import InlineError from '../../molecules/inlineError/InlineError';

type ToggleClickFunction = (data: EmailPreferencesModel, adobeTag: string) => Promise<void>;

interface MaskedAccountInfoSectionProps {
    data?: MaskedAccountInfoModel;
    isLoading?: boolean;
    error?: any;
}

interface ToggleSectionProps {
    data?: EmailPreferencesModel;
    isLoading?: boolean;
    error?: any;
    toggleClick: ToggleClickFunction;
}

export interface ManageEmailPreferencesProps {
    maskedSectionProps?: MaskedAccountInfoSectionProps;
    toggleSectionProps?: ToggleSectionProps;
}

function errorBlock(className: string): JSX.Element {
    return (
        <ErrorBlock
            text={LiteralsService.getLiteral('Budget.EmailPreferences.error')}
            anchorText={LiteralsService.getLiteral('Budget.EmailPreferences.tryAgain')}
            onClick={() => location?.reload()}
            className={className}
        />
    );
}

function errorTop(): JSX.Element {
    return (
        <div className={styles.topError}>
            <Typography
                variant={TypographyVariants.BODY}
                component="p"
                data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_TOP_SECTION}
            >
                {LiteralsService.getLiteral('Budget.EmailPreferences.weWillContactError')}
            </Typography>
            <InlineError
                text={LiteralsService.getLiteral('Budget.EmailPreferences.unableToDisplayEmail')}
                className={styles.topInlineError}
            />
        </div>
    );
}

function MaskedAccountInfoSection(props: MaskedAccountInfoSectionProps): JSX.Element {
    const { isLoading, error, data } = props;

    if (error) {
        return errorTop();
    }

    if (isLoading) {
        return <Loading className={styles.topLoading} />;
    }

    const email: string = data?.email;
    const preferredAccountMask: string = data?.preferredAccountMask;
    const changeEmailUrl: string = data?.changeEmailUrl;

    if (!email || !preferredAccountMask || !changeEmailUrl) {
        // We are considering it an error if any of the three fields are empty.
        // This might be too strict for lower environments.
        return errorTop();
    }

    return (
        <Typography
            variant={TypographyVariants.BODY}
            component="p"
            data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_TOP_SECTION}
        >
            {LiteralsService.getLiteral('Budget.EmailPreferences.weWillContact')}
            <Typography variant={TypographyVariants.BODY_BOLD} component="span">
                {email}
            </Typography>
            {LiteralsService.getLiteral('Budget.EmailPreferences.fromYourAccountEndingIn')}
            <Typography variant={TypographyVariants.BODY_BOLD} component="span">
                (-{preferredAccountMask})
            </Typography>
            <span>. </span>
            <Anchor
                href={changeEmailUrl}
                inline
                tag={adobeTags.Settings.UpdateEmail}
                data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_LINK}
            >
                {LiteralsService.getLiteral('Budget.EmailPreferences.visitYourAccountServices')}
            </Anchor>
        </Typography>
    );
}

function ToggleSection(props: ToggleSectionProps): JSX.Element {
    const { isLoading, error, data } = props;

    if (error) {
        return errorBlock(styles.bottomError);
    }

    const enableBudgetAlertEmail = data?.enableBudgetAlertEmail;
    const enableMonthlySummaryEmail = data?.enableMonthlySummaryEmail;
    const { toggleClick } = props;
    return (
        <div data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_BOTTOM_SECTION}>
            <Typography
                variant={TypographyVariants.SUBTEXT}
                component="p"
                className={`${styles.listHeader} ${colors.textWeak}`}
            >
                {LiteralsService.getLiteral('Budget.EmailPreferences.selectTheEmails')}
            </Typography>
            <div className={styles.preferencesList}>
                <ManageEmailPreferencesRow
                    title={LiteralsService.getLiteral('Budget.EmailPreferences.monthlySummaryEmail')}
                    subtitle={LiteralsService.getLiteral('Budget.EmailPreferences.aSummaryOfYour')}
                    isLoading={isLoading}
                    emailSelected={enableMonthlySummaryEmail}
                    onClick={async () => {
                        await toggleClick(
                            {
                                enableMonthlySummaryEmail: !enableMonthlySummaryEmail
                            },
                            enableMonthlySummaryEmail
                                ? adobeTags.Settings.MonthlyEmailToggleOff
                                : adobeTags.Settings.MonthlyEmailToggleOn
                        );
                    }}
                    className={styles.bottomBorder}
                />
                <ManageEmailPreferencesRow
                    title={LiteralsService.getLiteral('Budget.EmailPreferences.spendSmartUpdateEmail')}
                    subtitle={LiteralsService.getLiteral('Budget.EmailPreferences.anOverviewOfWhere')}
                    isLoading={isLoading}
                    emailSelected={enableBudgetAlertEmail}
                    onClick={async () => {
                        await toggleClick(
                            {
                                enableBudgetAlertEmail: !enableBudgetAlertEmail
                            },
                            enableBudgetAlertEmail
                                ? adobeTags.Settings.AlertEmailToggleOff
                                : adobeTags.Settings.AlertEmailToggleOn
                        );
                    }}
                    className="pad-0-b"
                />
            </div>
        </div>
    );
}

const title: JSX.Element = (
    <Typography
        variant={TypographyVariants.HEADING_S}
        className={`${colors.textStrong} ${styles.title}`}
        component="h2"
        data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_TITLE}
    >
        {LiteralsService.getLiteral('Budget.EmailPreferences.emailNotifications')}
    </Typography>
);

export function ManageEmailPreferencesComponent({
    maskedSectionProps,
    toggleSectionProps
}: ManageEmailPreferencesProps): JSX.Element {
    if (maskedSectionProps?.isLoading && toggleSectionProps?.isLoading) {
        return (
            <div className={styles.container} data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_LOADING}>
                {title}
                <Loading className={styles.bothSectionsLoading} />
            </div>
        );
    }

    if (maskedSectionProps?.error && toggleSectionProps?.error) {
        return (
            <div className={styles.container} data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT_ERROR}>
                {title}
                {errorBlock(styles.bothSectionsError)}
            </div>
        );
    }

    return (
        <div
            className={styles.manageEmailPreferencesContainer}
            data-cy={Selectors.SpendSmart.EMAIL_PREFERENCES_COMPONENT}
        >
            {title}
            <MaskedAccountInfoSection {...maskedSectionProps} />
            <ToggleSection {...toggleSectionProps} />
        </div>
    );
}

export default function ManageEmailPreferences(): JSX.Element {
    try {
        const emailPreferencesResult = useEmailPreferences();
        const { mutateAsync: updateEmailPreferences } = useUpdateEmailPreferences();
        const maskedAccountInfoResult = useMaskedAccountInfo();
        const handleToggleClick = async (newPrefs: EmailPreferencesModel, adobeTag: string): Promise<void> => {
            await updateEmailPreferences(newPrefs);
            trackClick(location.pathname, adobeTag);
        };
        const componentProps: ManageEmailPreferencesProps = {
            maskedSectionProps: {
                data: maskedAccountInfoResult.data,
                isLoading: maskedAccountInfoResult.isLoading,
                error: maskedAccountInfoResult.error
            },
            toggleSectionProps: {
                data: emailPreferencesResult.data,
                isLoading: emailPreferencesResult.isLoading,
                error: emailPreferencesResult.error,
                toggleClick: handleToggleClick
            }
        };
        return <ManageEmailPreferencesComponent {...componentProps} />;
    } catch (error) {
        return errorBlock(styles.bothSectionsError);
    }
}
